export type ComposerSelection = {start: number; end: number};

export type WrapSelectionResult = {
    text: string;
    selection: ComposerSelection;
};

export const BOLD_MARKER = '*';
export const ITALIC_MARKER = '_';
export const STRIKETHROUGH_MARKER = '~';
export const CODE_MARKER = '`';

export type FormattingMarker =
    | typeof BOLD_MARKER
    | typeof ITALIC_MARKER
    | typeof STRIKETHROUGH_MARKER
    | typeof CODE_MARKER;

const FENCE = '```';
const OPEN_FENCE_REGEX = /^```/;

function collapsed(at: number): ComposerSelection {
    return {start: at, end: at};
}

function fencedBlockBody(core: string): string | null {
    const lines = core.split('\n');
    if (lines.length < 2) {
        return null;
    }
    if (!OPEN_FENCE_REGEX.test(lines[0])) {
        return null;
    }
    if (lines[lines.length - 1].trim() !== FENCE) {
        return null;
    }
    return lines.slice(1, -1).join('\n');
}

function surroundingFenceLines(
    text: string,
    coreStart: number,
    coreEnd: number,
): {from: number; to: number} | null {
    if (coreStart < 1 || text[coreStart - 1] !== '\n') {
        return null;
    }
    if (coreEnd >= text.length || text[coreEnd] !== '\n') {
        return null;
    }
    const previousLineStart = text.lastIndexOf('\n', coreStart - 2) + 1;
    if (!OPEN_FENCE_REGEX.test(text.slice(previousLineStart, coreStart - 1))) {
        return null;
    }
    const nextLineBreak = text.indexOf('\n', coreEnd + 1);
    const nextLineEnd = nextLineBreak === -1 ? text.length : nextLineBreak;
    if (text.slice(coreEnd + 1, nextLineEnd).trim() !== FENCE) {
        return null;
    }
    return {from: previousLineStart, to: nextLineEnd};
}

export function wrapSelection(
    text: string,
    start: number,
    end: number,
    marker: FormattingMarker,
): WrapSelectionResult {
    const from = Math.max(0, Math.min(start, end));
    const to = Math.min(text.length, Math.max(start, end));

    if (from >= to) {
        return {text, selection: {start, end}};
    }

    const raw = text.slice(from, to);
    const core = raw.trim();
    if (core === '') {
        return {text, selection: {start, end}};
    }

    const coreStart = from + raw.indexOf(core);
    const coreEnd = coreStart + core.length;
    const before = text.slice(0, coreStart);
    const after = text.slice(coreEnd);

    if (marker === CODE_MARKER) {
        const body = fencedBlockBody(core);
        if (body !== null) {
            return {
                text: before + body + after,
                selection: collapsed(coreStart + body.length),
            };
        }
        const fences = surroundingFenceLines(text, coreStart, coreEnd);
        if (fences) {
            const rebuilt = text.slice(0, fences.from) + core + text.slice(fences.to);
            return {
                text: rebuilt,
                selection: collapsed(fences.from + core.length),
            };
        }
    }

    if (
        core.length > 2 * marker.length &&
        core.startsWith(marker) &&
        core.endsWith(marker)
    ) {
        const inner = core.slice(marker.length, core.length - marker.length);
        return {
            text: before + inner + after,
            selection: collapsed(coreStart + inner.length),
        };
    }

    if (
        text.slice(coreStart - marker.length, coreStart) === marker &&
        text.slice(coreEnd, coreEnd + marker.length) === marker
    ) {
        const rebuilt =
            text.slice(0, coreStart - marker.length) +
            core +
            text.slice(coreEnd + marker.length);
        return {
            text: rebuilt,
            selection: collapsed(coreStart - marker.length + core.length),
        };
    }

    if (marker === CODE_MARKER && core.includes('\n')) {
        const openingBreak =
            coreStart === 0 || text[coreStart - 1] === '\n' ? '' : '\n';
        const closingBreak =
            coreEnd === text.length || text[coreEnd] === '\n' ? '' : '\n';
        const block = `${openingBreak}${FENCE}\n${core}\n${FENCE}`;
        return {
            text: before + block + closingBreak + after,
            selection: collapsed(coreStart + block.length),
        };
    }

    const wrapped = `${marker}${core}${marker}`;
    return {
        text: before + wrapped + after,
        selection: collapsed(coreStart + wrapped.length),
    };
}
